import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CampaniasGlobalService } from '../../../services/global-campanias.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { SolicitudCamService } from '../../datosbasicos/solicitud-cam.service';
import { Observable, catchError, of, tap } from 'rxjs';
import { TwSolicitudCamViewModel } from '../../../types/solicitudcam-datosbasicos.types';
import { TCampaniaEspecificaViewModel } from 'src/app/components/_mantenimiento/campanias/campaniasespecificas/campaniaespecifica.types';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { DocumentosSolicitudCamService } from '../solicitud-documentacion.service';
import { DocumentoSolicitudCamViewModel, EditionMode } from '../solicitud-documentacion.types';
import { TwDocumentacionCamViewModel } from 'src/app/components/_mantenimiento/campanias/campania/documentacioncam/documentacioncam.types';
import { DialogComponent } from 'src/app/components/core/components/dialog/dialog.component';
import { ConfirmationService } from 'primeng/api';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';

@Component({
    selector: 'app-solicitud-documentacion-wrapper',
    templateUrl: './solicitud-documentacion-wrapper.component.html',
    styleUrls: ['./solicitud-documentacion-wrapper.component.scss'],
})
export class SolicitudDocumentacionWrapperComponent extends ComponentBase implements OnInit, OnDestroy {
    @HostListener('window:beforeunload', ['$event'])
    forceCheckUnload(): boolean {
        // Prevenimos la pérdida de datos, si el usuario recarga accidentalmente la página.
        // Lo controlamos y el navegador le requiere confirmación para finalizar la recarga.
        this.purgeNotAttachedFiles();
        return false;
    }

    visibilitySpinner = false;
    visibilitySpinnerEliminando = false;

    solicitudCam: TwSolicitudCamViewModel;
    campaniaEspecifica: TCampaniaEspecificaViewModel;
    documentosSolicitudCam: DocumentoSolicitudCamViewModel[];
    documentosObligatoriosSinAdjuntar: number | null = null;

    formGroup: FormGroup;
    readonly: boolean;
    prefixFormControlNameTipoDoc = 'tipodocumentacion_'; // Id de los campos que se utilizan para mostrar los tipos de documentos

    /************************************************************************************
     * A continuación se listan variables usadas y relacionadas con la ventana de diálogo
     * que permite editar/crear documentación para la solicitud de campaña.
     * *********************************************************************************/
    @ViewChild(DialogComponent) private dialogComponent: DialogComponent;
    mode = EditionMode.Creation;
    dialogTitle = 'Añadir Documentación a la Solicitud de Campaña';
    dialogComponentVisible = false;
    docSelectedToEdit: DocumentoSolicitudCamViewModel | null = null;
    private filePendingToPurge = '';
    // Si se cierra la ventana, habiendo subido un fichero, pero no se ha guardado la documentación, se eliminará el fichero de la BD.
    dialogAceptarDisabled = true; // Permite habilitar/deshabilitar el botón aceptar en la ventana de diálogo

    /***********************************************************************************
     * Fin variables relacionadas con la ventana de diálogo
     **********************************************************************************/

    private listUrl = '/campanias/solicitudes';
    private successMessage = 'La documentación se ha borrado correctamente';
    private loseChangesQuestion = '¿Desea perder los cambios pendientes?';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idSolicitudCam: any = params.get('id');
        if (idSolicitudCam) {
            this.getSolicitudCam(parseInt(idSolicitudCam));
        }
    });

    constructor(
        private activatedRoute: ActivatedRoute,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private router: Router,
        private solicitudCamService: SolicitudCamService,
        private navMenuService: NavMenuService,
        private campaniaGlobalService: CampaniasGlobalService,
        private documentosSolicitudCamService: DocumentosSolicitudCamService,
        private confirmationService: ConfirmationService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        // NO-OP
    }

    /**
     * Redirigimos al usuario a la lista de solicitudes de campaña.
     */
    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    /**
     * Este método obtiene los datos de la solicitud indicada por parámetro.
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de recintos de la solicitud.
     * @param idSolicitudCam number
     */
    private getSolicitudCam(idSolicitudCam: number): void {
        this.visibilitySpinner = true;
        this.solicitudCamService
            .getSolicitudCam(idSolicitudCam)
            .pipe(
                tap((v: TwSolicitudCamViewModel) => {
                    this.visibilitySpinner = false;
                    this.solicitudCam = v;
                    this.navMenuService.updateMenuServiceForSolicitudCam(v.Id!, v.IdEstadoSolicitudCam!, v.IdTipoSolicitudCam);
                    this.readonly = this.checkReadOnlyConditions();
                    if (this.solicitudCam) {
                        this.loadCampaniaData(this.solicitudCam.IdCampania!);
                    }
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup?.disable();
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * Cargamos los datos asociados a la campaña a la que está vinculada la solicitud de campaña.
     * Lo que utilizaremos será la configuración para la documentación.
     * @param idCampania number
     */
    private loadCampaniaData(idCampania: number): void {
        this.visibilitySpinner = true;
        this.campaniaGlobalService
            .getCampaniaById(idCampania)
            .pipe(
                tap((ce: TCampaniaEspecificaViewModel) => {
                    this.visibilitySpinner = false;
                    this.campaniaEspecifica = ce;
                    this.loadDocumentosSolicitudCam(this.solicitudCam?.Id || 0);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup?.disable();
                    return of([]);
                }),
            )
            .subscribe(); // Sin subscribe, no se ejecuta la invocación al servicio
    }

    /**
     * Cargamos la documentación asociada a la solicitud de campaña.
     * @param idSolicitudCam number
     */
    private loadDocumentosSolicitudCam(idSolicitudCam: number): void {
        this.visibilitySpinner = true;
        this.documentosSolicitudCamService
            .getDocumentosSolicitudCam(idSolicitudCam)
            .pipe(
                tap((docsSolicitudCamVM: DocumentoSolicitudCamViewModel[]) => {
                    this.visibilitySpinner = false;
                    this.documentosSolicitudCam = docsSolicitudCamVM;
                    this.getDocumentosObligatoriosSinAdjuntar();
                    this.buildForm();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup?.disable();
                    return of([]);
                }),
            )
            .subscribe(); // Sin subscribe, no se ejecuta la invocación al servicio
    }

    /**
     * Se crea el formulario con dos controles básicos:
     * - IdCampaniaDescripcion
     * - AdjuntarDocumentacion
     * Si el campo AdjuntarDocumentacion (de la configuración de la campaña) está a true,
     * se añadirá un control por cada tipo de documentación configurado para la misma.
     * De la misma forma se hará con los documentos que el usuario vaya añadiendo, que en función del campo Obligatoria en TwDocumentacionCam, se añadirá la validación required o no.
     */
    private buildForm(): void {
        this.formGroup = new FormGroup({});
        this.formGroup.addControl('IdCampaniaDescripcion', new FormControl({ value: '', disabled: true }));
        this.formGroup.addControl('AdjuntarDocumentacion', new FormControl({ value: '', disabled: true }));

        if (this.campaniaEspecifica?.AdjuntarDocumentacion && this.campaniaEspecifica?.Documentacion?.length > 0) {
            this.addControlForEachDocumentoConfigurado(this.campaniaEspecifica?.Documentacion, this.formGroup);
        }

        this.form = this.formGroup;
    }

    /**
     * Se añade un control al formulario general, para cada uno de los tipos de documentos configurados para la campaña.
     * @param docsCam TwDocumentacionCamViewModel[]
     * @param _formGroup FormGroup
     */
    private addControlForEachDocumentoConfigurado(docsCam: TwDocumentacionCamViewModel[], _formGroup: FormGroup): void {
        docsCam.forEach(docCam => {
            let controlName = this.prefixFormControlNameTipoDoc + docCam.IdTipoDocumentacion;
            if (docCam.Obligatoria) {
                _formGroup.addControl(controlName, new FormControl({ value: docCam.Obligatoria, disabled: true }, Validators.required));
            } else {
                _formGroup.addControl(controlName, new FormControl({ value: docCam.Obligatoria, disabled: true }));
            }
        });
    }

    /*******************************************************************************************************/
    /* Métodos y handlers relacionados con la lista de documentaciones asociadas a la Solicitud de Campaña */
    /*******************************************************************************************************/

    /**
     * Se marca la documentación como "borrada"
     * @param docSelected DocumentoSolicitudCamViewModel
     * @returns void
     */
    onRemoveDoc(docSelected: DocumentoSolicitudCamViewModel): void {
        const doc = this.documentosSolicitudCam?.find(doc => doc.Id === docSelected.Id);
        if (!doc) {
            return;
        }

        doc.MarkToDelete = 1;
    }

    /**
     * Se marca la documentación como "no borrada"
     * @param docSelected DocumentoSolicitudCamViewModel
     * @returns void
     */
    onUndoRemoveDoc(docSelected: DocumentoSolicitudCamViewModel): void {
        const doc = this.documentosSolicitudCam?.find(doc => doc.Id === docSelected.Id);
        if (!doc) {
            return;
        }

        doc.MarkToDelete = 0;
    }

    /**
     * Se muestra la ventana de diálogo que permite crear/editar la documentación que se asociará a la solicitud de campaña.
     */
    onEditDoc(docSelected: DocumentoSolicitudCamViewModel): void {
        this.docSelectedToEdit = { ...docSelected };
        this.showDialog(EditionMode.Edition);
    }

    /**
     * Se muestra la ventana de diálogo que permite visualizar la documentación que se asociará a la solicitud de campaña.
     */
    onViewDoc(docSelected: DocumentoSolicitudCamViewModel): void {
        this.docSelectedToEdit = { ...docSelected };
        this.showDialog(EditionMode.Edition);
    }

    /*****************************************************************************************************************************************/
    /* Métodos y handlers relacionados con la ventana de diálogo que permite añadir/editar documentación asociada a la Solicitud de Campaña. */
    /*****************************************************************************************************************************************/

    /**
     * Se muestra la ventana de diálogo que permite crear/editar la documentación que se asociará a la solicitud de campaña.
     */
    onClickAddDoc(): void {
        this.docSelectedToEdit = null;
        this.showDialog(EditionMode.Creation);
    }

    /**
     * Permite marcar como borrados las documentaciones marcadas en la lista.
     * Se enviará una petición al core, para que sean borrados de la BD, junto con sus ficheros asociados.
     */
    onClickDeleteDocs(): void {
        this.visibilitySpinnerEliminando = true;
        this.visibilitySpinner = true;
        const docsToDelete = this.documentosSolicitudCam.filter(doc => doc?.MarkToDelete).map(doc => doc?.Id);
        this.documentosSolicitudCamService
            .deleteDocumentaciones(docsToDelete)
            .pipe(
                tap(() => {
                    this.visibilitySpinnerEliminando = false;
                    this.visibilitySpinner = false;
                    this.filePendingToPurge = '';
                    this.addSuccessMessage(this.successMessage);
                    this.getSolicitudCam(this.solicitudCam.Id!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinnerEliminando = false;
                    this.visibilitySpinner = false;
                    this.formGroup?.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * When the user selects "Aceptar" in the dialog, 'accepted' will be true
     * @param accepted boolean
     */
    onDialogAccepted(accepted: boolean): void {
        this.closeDialog();
        this.mode = EditionMode.Creation;
        this.docSelectedToEdit = null;
        if (accepted) {
            this.filePendingToPurge = '';
        }
        this.dialogAceptarDisabled = true;
        this.getSolicitudCam(this.solicitudCam.Id!);
    }

    /**
     * Detectamos el evento que indica que se ha guardado la documentación (y su fichero asociado).
     * En éste caso cerramos la ventana de diálogo e inicializamos la variable fileToPurge.
     * Esta variable fileToPurge nos permite controlar si la ventana de diálogo se ha cerrado sin guardar la documentación.
     */
    onDocumentacionSaved($event: any): void {
        this.dialogAceptarDisabled = false;
        this.onDialogAccepted(true);
    }

    /**
     * Guardamos el guid (codigo-storage) del fichero que se ha subido usando la pantalla de diálogo de creación de la documentación.
     * Puede ocurrir que se cierre la ventana sin haber guardado la documentación.
     * Si detectamos ése caso, se purga el fichero, esto es, se elimina de la BD.
     */
    onFileUpload(guid: string): void {
        if (this.filePendingToPurge !== '') {
            this.purgeNotAttachedFiles();
        }
        this.filePendingToPurge = guid;
    }

    onFilePurged(guid: string): void {
        this.filePendingToPurge = '';
    }

    /**
     * Mostramos la ventana de diálogo que permitirá editar/crear una documentación para la solicitud de campaña.
     * @param mode EditionMode
     */
    private showDialog(mode: EditionMode): void {
        this.mode = mode;
        this.dialogComponentVisible = true;
        setTimeout(() => {
            // To give time so the dialog is initialized
            this.dialogComponent.showDialog();
        });
    }

    private closeDialog(): void {
        this.dialogComponent.closeDialog();
        this.dialogComponentVisible = false;
    }

    /****************************************************************************************************************/
    /* Métodos y getters que permiten controlar los permisos del usuario, calcular la propiedad readonly y disabled */
    /****************************************************************************************************************/

    get addingNotAllowed(): boolean {
        // readonly se calcula en checkReadOnlyConditions, que utiliza loggedInUser?.getAction
        return this.visibilitySpinner || this.readonly;
    }

    get deletingNotAllowed(): boolean {
        // readonly se calcula en checkReadOnlyConditions, que utiliza loggedInUser?.getAction
        return !this.docsToDelete || this.visibilitySpinner || this.readonly;
    }

    get updatingNotAllowed(): boolean {
        // readonly se calcula en checkReadOnlyConditions, que utiliza loggedInUser?.getAction
        const hasAccessToUpdate =
            this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.UPDATE_DOC_SOL_CAM, this.solicitudCam.IdEstadoSolicitudCam).Editable || false;

        return this.visibilitySpinner || !hasAccessToUpdate;
    }

    private get userIsOwner(): boolean {
        return this.isOperadorCam && this.solicitudCam && this.loggedInUser?.Id === this.solicitudCam?.IdUsuario;
    }

    /**
     * Indica si hay documentaciones de la solicitud que hayan sido marcados para borrar.
     */
    get docsToDelete(): boolean {
        const docsToDelete = this.documentosSolicitudCam?.filter(doc => doc.MarkToDelete);
        return docsToDelete?.length > 0;
    }

    /**
     * Este componente se bloqueará en modo solo lectura cuando el usuario actual no sea uno de los permitidos para trabajar con campañas.
     * Si el usuario no es el propietario y no es el admin, también se bloquea.
     * Además el estado de la solicitud tendrá que ser uno de los permitidos para la edición.
     * @returns boolean
     */
    private checkReadOnlyConditions(): boolean {
        const hasAccessToAdd = this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.ADD_DOC_SOL_CAM, this.solicitudCam.IdEstadoSolicitudCam).Editable || false;
        const hasAccessToDelete =
            this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.REMOVE_DOC_SOL_CAM, this.solicitudCam.IdEstadoSolicitudCam).Editable || false;
        const hasAccessToUpdate =
            this.loggedInUser?.getAction(EnumCodigosTwTcAcciones.UPDATE_DOC_SOL_CAM, this.solicitudCam.IdEstadoSolicitudCam).Editable || false;

        if (!hasAccessToAdd || !hasAccessToDelete || !hasAccessToUpdate) {
            return true;
        }

        if (!this.esPeriodoVigente && this.solicitudCam?.PeriodoPresentacionVigente === false) {
            return true;
        }

        if (this.isAdmin) {
            return false;
        }

        return !this.userIsOwner;
    }

    getDocumentosObligatoriosSinAdjuntar(): number {
        if (this.campaniaEspecifica?.Documentacion === null || this.documentosSolicitudCam === null) return 0;
        this.documentosObligatoriosSinAdjuntar = this.documentosSolicitudCamService.getDocumentosObligatoriosSinAdjuntar(
            this.campaniaEspecifica?.Documentacion,
            this.documentosSolicitudCam,
        );
        return this.documentosObligatoriosSinAdjuntar;
    }

    get esPeriodoVigente(): boolean {
        return this.solicitudCam?.PeriodoVigente;
    }

    /***********************************************************************************************************************************************/
    /* Métodos para controlar que si se cierra accidentalmente la ventana y se han quedado ficheros sin vincular a la documentación, sean borrados */
    /***********************************************************************************************************************************************/

    /**
     * Si el usuario ha subido ficheros, pero no se han guardado en la solicitud, los purgamos de la BD.
     * Sabemos que aquellos "adjuntos" que no tienen Id, no existen en la BD, así que se descartan directamente.
     */
    private purgeNotAttachedFiles(): void {
        if (!this.filePendingToPurge || this.readonly) {
            return;
        }
        this.documentosSolicitudCamService.deleteFile(this.filePendingToPurge).subscribe();
        this.filePendingToPurge = '';
    }

    // Usado en solicitudes-cam-routing.module.ts
    async canDeactivate(): Promise<boolean | Observable<boolean>> {
        setTimeout(() => {
            //
        }, 100);
        if (this.filePendingToPurge) {
            const response = await this.toLoseChangesQuestion();
            return response;
        } else {
            return true;
        }
    }

    private async toLoseChangesQuestion(): Promise<boolean> {
        return new Promise(resolve => {
            this.confirmationService.confirm({
                message: this.loseChangesQuestion,
                accept: () => {
                    this.purgeNotAttachedFiles();
                    resolve(true);
                },
                reject: () => {
                    resolve(false);
                },
            });
        });
    }

    ngOnDestroy(): void {
        this.purgeNotAttachedFiles();
    }
}
